import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Card, Box, Typography, Tabs, Tab, CircularProgress, alpha, useTheme, Avatar } from "@mui/material";
import { useQuery } from "react-query";
import {
  Person as PersonIcon,
  ContactPhone as ContactIcon,
  Info as InfoIcon,
  PhotoCamera as PhotoIcon,
  Inventory as InventoryIcon,
  WarningAmber as AllergyIcon,
  Description as DocumentIcon,
  EventNote as EventIcon,
  Home as HomeIcon,
  Assessment as AssessmentIcon,
  AttachMoney as MoneyIcon,
} from "@mui/icons-material";
import ContactAndFamily from "../../../components/Clients/ContactAndFamily/ContactAndFamily";
import Photographs from "../../../components/Clients/Photographs/Photographs";
import AboutMe from "../../../components/Clients/AboutMe/AboutMe";
import PersonalInventory from "../../../components/Clients/PersonalInventory/PersonalInventory";
import Allergies from "../../../components/Clients/Allergies/Allergies";
import Documents from "../../../components/Clients/Documents/Documents";
import RecordAbsences from "../../../components/Clients/RecordAbsences/RecordAbsences";
import DomCare from "../../../components/Clients/DomCare/DomCare";
import Funding from "../../../components/Clients/Funding/Funding";
import InitialAssessment from "../../../components/Clients/InitialAssessment/InitialAssessment";
import { fetchClient } from "../../../api/services/Client";
import { QK } from "../../../base/qk";
import ClientUpdate from "../../../components/Clients/UpdateClientDetails/ClientUpdate";

const CustomAvatar = ({ firstName, lastName, size = 64 }) => {
  const theme = useTheme();
  const getInitials = (first, last) => {
    const firstInitial = first?.charAt(0) || '';
    const lastInitial = last?.charAt(0) || '';
    return `${firstInitial}${lastInitial}`.toUpperCase();
  };

  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        bgcolor: theme.palette.primary.main,
        fontSize: size * 0.4,
        fontWeight: 600,
        color: '#fff',
        boxShadow: `0px 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
      }}
    >
      {getInitials(firstName, lastName)}
    </Avatar>
  );
};

const ClientPersonalDetails = () => {
  const { id } = useParams();
  const { hash } = useLocation();
  const theme = useTheme();

  const { data, isLoading } = useQuery([QK.client, id], () => fetchClient(id), {
    enabled: !!id,
  });

  const getActiveTabFromHash = () => {
    const currentHash = hash.replace("#", "");
    return currentHash || "personal-details";
  };

  const [selectedTab, setSelectedTab] = useState(getActiveTabFromHash());

  useEffect(() => {
    setSelectedTab(getActiveTabFromHash());
  }, [hash]);

  const handleTabChange = (_, newValue) => {
    window.location.hash = newValue;
  };

  const profilePhotoUrl = data?.profile_photo || 
                          data?.profile_image || 
                          data?.image || 
                          data?.avatar;

  const tabs = [
    { value: "personal-details", label: "Personal", icon: <PersonIcon sx={{ fontSize: 18 }} /> },
    { value: "contacts-family", label: "Contacts", icon: <ContactIcon sx={{ fontSize: 18 }} /> },
    { value: "about-me", label: "About", icon: <InfoIcon sx={{ fontSize: 18 }} /> },
    { value: "photographs", label: "Photos", icon: <PhotoIcon sx={{ fontSize: 18 }} /> },
    { value: "personal-inventory", label: "Inventory", icon: <InventoryIcon sx={{ fontSize: 18 }} /> },
    { value: "allergies", label: "Allergies", icon: <AllergyIcon sx={{ fontSize: 18 }} /> },
    { value: "documents", label: "Docs", icon: <DocumentIcon sx={{ fontSize: 18 }} /> },
    { value: "record-absences", label: "Absences", icon: <EventIcon sx={{ fontSize: 18 }} /> },
    { value: "dom-care", label: "Dom Care", icon: <HomeIcon sx={{ fontSize: 18 }} /> },
    { value: "initial-assessment", label: "Assessment", icon: <AssessmentIcon sx={{ fontSize: 18 }} /> },
    { value: "funding", label: "Funding", icon: <MoneyIcon sx={{ fontSize: 18 }} /> },
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", pb: 3 }}>
      <Card
        sx={{
          mb: 2.5,
          borderRadius: "12px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.5,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
            {profilePhotoUrl ? (
              <Avatar
                src={profilePhotoUrl}
                alt={`${data?.first_name} ${data?.last_name}`}
                sx={{
                  width: 64,
                  height: 64,
                  boxShadow: `0px 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                }}
              />
            ) : (
              <CustomAvatar 
                firstName={data?.first_name} 
                lastName={data?.last_name}
                size={64}
              />
            )}
            <Box>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: "#1A1A2E" }}
              >
                {data?.first_name} {data?.last_name}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#667085" }}
              >
                NHS: {data?.nhs_number || "Not Set"} • ID: #{id}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              px: 2,
              py: 0.75,
              borderRadius: "20px",
              bgcolor: alpha(theme.palette.success.main, 0.08),
              display: "flex",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: "#10B981",
              }}
            />
            <Typography
              variant="body2"
              sx={{ color: "#039855", fontWeight: 600 }}
            >
              Active
            </Typography>
          </Box>
        </Box>
      </Card>

      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 2, pt: 1.5 }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 48,
              "& .MuiTab-root": {
                minHeight: 44,
                minWidth: "auto",
                py: 1,
                px: 2.5,
                fontSize: "0.8rem",
                fontWeight: 500,
                textTransform: "none",
                color: "#667085",
                borderRadius: "8px",
                "&.Mui-selected": {
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                },
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              },
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px",
                bgcolor: theme.palette.primary.main,
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {tab.icon}
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: "inherit" }}>
                      {tab.label}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ p: 2.5, pt: 2 }}>
          {selectedTab === "personal-details" && <ClientUpdate />}
          {selectedTab === "contacts-family" && <ContactAndFamily />}
          {selectedTab === "about-me" && <AboutMe />}
          {selectedTab === "photographs" && <Photographs />}
          {selectedTab === "personal-inventory" && <PersonalInventory />}
          {selectedTab === "allergies" && <Allergies />}
          {selectedTab === "documents" && <Documents />}
          {selectedTab === "record-absences" && <RecordAbsences />}
          {selectedTab === "dom-care" && <DomCare />}
          {selectedTab === "initial-assessment" && <InitialAssessment />}
          {selectedTab === "funding" && <Funding />}
        </Box>
      </Card>
    </Box>
  );
};

export default ClientPersonalDetails;